import React, { useCallback, useContext, useEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import AppContext, { PermissionContext } from 'context/Context';
import Logo from 'components/common/Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import { capitalize } from 'helpers/utils';
import NavbarTopDropDownMenus from 'components/layouts/navbar/top/NavbarTopDropDownMenus';
import {filterMenusByPermissions} from "utils";
import {publicRoutes, protectedRoutes} from "routes/siteMaps";
import '../navbar-tw.scss';

const NavbarVertical = () => {
  const {
    config: {
      navbarPosition,
      navbarStyle,
      isNavbarVerticalCollapsed,
      showBurgerMenu
    },
    setConfig
  } = useContext(AppContext);

  const closeBurgerMenu = useCallback(() => setConfig('showBurgerMenu', false), [setConfig]);
  const navRef = useRef(null);

  // Mobile menu is a viewport-covering sheet: Escape closes it, and Tab is
  // trapped inside it (below xl the page behind is dimmed and inert-looking,
  // so focus must not wander into it). Desktop (≥1280px) never sets
  // showBurgerMenu, so the trap only runs for the mobile sheet.
  useEffect(() => {
    if (!showBurgerMenu) return undefined;
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        closeBurgerMenu();
        return;
      }
      if (e.key !== 'Tab' || !navRef.current) return;
      const focusables = navRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!navRef.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showBurgerMenu, closeBurgerMenu]);

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  const { permissions } = useContext(PermissionContext);

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  // Hooks must all run before this guard (rules-of-hooks).
  if (!permissions) {
    return null;
  }

  let allowedRoutes = filterMenusByPermissions(
    protectedRoutes,
    permissions
  );

  allowedRoutes = allowedRoutes.concat(publicRoutes);

  // Sort the routes by sequence number
  allowedRoutes = allowedRoutes.sort((a, b) => a.sequence - b.sequence);

  //Control mouseEnter event
  let time = null;
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  const handleMouseLeave = () => {
    clearTimeout(time);
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  const NavbarLabel = ({ label }) => (
    <li className="ptl-nav-item">
      <div className="tw-flex tw-items-center tw-mt-3 tw-mb-2 ptl-navbar-vertical-label-wrapper">
        <div className="tw-shrink-0 ptl-navbar-vertical-label">
          {label}
        </div>
        <div className="tw-grow tw-pl-2">
          <hr className="tw-mb-0 ptl-navbar-vertical-hr"></hr>
        </div>
      </div>
    </li>
  );

  return (
    <nav
      ref={navRef}
      className={classNames('ptl-navbar-vertical', {
        [`ptl-navbar-vertical--${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
    >
      <div className="tw-hidden xl:tw-flex tw-items-center">
        <ToggleButton />
        <Logo
          at="navbar-vertical"
          width={72}
          className="tw-flex tw-items-center tw-py-4"
          textClass="tw-text-xl tw-font-bold"
          style={{ textDecoration: 'none' }}
        />
      </div>
      {/* Mobile backdrop: dims the page behind the burger menu; click closes.
          Desktop (≥ xl) never shows it — the sidebar is permanent there. */}
      {showBurgerMenu && (
        <div
          className="xl:tw-hidden tw-fixed tw-inset-x-0 tw-bottom-0 tw-bg-slate-900/40"
          style={{ top: 'var(--topnav-offset)' }}
          onClick={closeBurgerMenu}
          aria-hidden
        />
      )}
      <div
        className={classNames('ptl-navbar-vertical-collapse tw-relative', {
          'ptl-show': showBurgerMenu
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? 'linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2)'
              : 'none'
        }}
      >
        <div className="ptl-navbar-vertical-content">
          <ul className="ptl-navbar-nav tw-flex tw-flex-col tw-pl-0 tw-mb-0 tw-list-none">
            {allowedRoutes.map(route => (
              <Fragment key={route.label}>
                {!route.labelDisable && (
                  <NavbarLabel label={capitalize(route.label)} />
                )}
                <NavbarVerticalMenu routes={route.children} />
              </Fragment>
            ))}
          </ul>

          <>
            {navbarPosition === 'combo' && (
              <div className="lg:tw-hidden">
                <div className="ptl-navbar-vertical-divider">
                  <hr className="ptl-navbar-vertical-hr tw-my-2" />
                </div>
                <div className="tw-flex tw-flex-col">
                  <NavbarTopDropDownMenus />
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </nav>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string
};

export default NavbarVertical;
